import fp from 'lodash/fp';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useRef } from 'react';
import Autosuggest from 'react-autosuggest';
import ArrowPrimary from '../../Icons/Arrow-Primary.svg';
import defaultStyles from '../common/dropdown.module.scss';
import TextInput from '../textinput/TextInput';
import { SuggestionsContainer, makeRenderSuggestion } from '../common/dropdownComponents';

const Typeahead = ({
    disabled,
    getDisplayValue,
    getKeyValue,
    height,
    maxSuggestions,
    onSelection,
    options,
    placeholder,
    styles,
    textInputStyles,
    value,
    allowFreeText,
    portal
}) => {
    const inputRef = useRef(null);
    const [localOptions, setOptions] = useState(options);
    const [localValue, setValue] = useState(value);

    const onSuggestionsFetchRequested = useCallback(({ value }) => {
        const matchingOptions = options.filter(
            (item) => getDisplayValue(item)
                .toLowerCase()
                .includes(value.toLowerCase())
        );

        setOptions(
            matchingOptions.length < maxSuggestions
                ? matchingOptions
                : matchingOptions.slice(0, maxSuggestions)
        );
    }, [getDisplayValue, maxSuggestions, options]);

    const onSuggestionSelected = useCallback(
        (event, { suggestion }) => onSelection(getKeyValue(suggestion)), [onSelection, getKeyValue]
    );
    const renderSuggestionsContainer = (props) => (
        <SuggestionsContainer
            { ...props }
            portal={ portal }
            height={ height }
            styles={ styles }
            inputRef={ inputRef.current }
        />
    );
    const onChange = useCallback((e, { newValue }) => {
        setValue(newValue);
        if (allowFreeText) {
            onSelection(newValue);
        }
    }, [allowFreeText, onSelection]);

    return (
        <Autosuggest
            suggestions={ localOptions }
            getSuggestionValue={ getDisplayValue }
            renderSuggestion={ makeRenderSuggestion(getDisplayValue, styles) }
            inputProps={ {
                onChange,
                value: localValue,
                disabled
            } }
            onSuggestionSelected={ onSuggestionSelected }
            onSuggestionsFetchRequested={ onSuggestionsFetchRequested }
            onSuggestionsClearRequested={ fp.noop }
            renderSuggestionsContainer={ renderSuggestionsContainer }
            shouldRenderSuggestions={ () => true }
            focusInputOnSuggestionClick={ false }
            renderInputComponent={ (inputProps) => (
                <>
                    <TextInput
                        styles={ textInputStyles }
                        icon={ ArrowPrimary }
                        inputProps={ inputProps }
                        onChange={ inputProps.onChange }
                        value={ inputProps.value }
                        placeholder={ placeholder }
                        disabled={ inputProps.disabled }
                    />
                    <div ref={ inputRef } className={ styles.dropdownAnchor } />
                </>
            ) }
        />
    );
};

const valuePropType = PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
]);

Typeahead.propTypes = {
    allowFreeText: PropTypes.bool,
    onSelection: PropTypes.func.isRequired,
    styles: PropTypes.objectOf(PropTypes.string),
    textInputStyles: PropTypes.shape({}),
    placeholder: PropTypes.string,
    getKeyValue: PropTypes.func,
    getDisplayValue: PropTypes.func,
    disabled: PropTypes.bool,
    options: PropTypes.arrayOf(valuePropType),
    value: PropTypes.string,
    maxSuggestions: PropTypes.number,
    height: PropTypes.number,
    portal: PropTypes.bool
};

Typeahead.defaultProps = {
    allowFreeText: false,
    styles: defaultStyles,
    textInputStyles: undefined,
    getKeyValue: fp.identity,
    getDisplayValue: fp.identity,
    disabled: false,
    options: [],
    value: '',
    maxSuggestions: Infinity,
    height: 200,
    placeholder: '',
    portal: false
};

export default Typeahead;
